import { useState, useEffect } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { 
  Users, Shield, TrendingUp, Award, UserPlus, Trash2, Edit, 
  CheckCircle, XCircle, AlertTriangle, BarChart2, DollarSign,
  FileText, Calendar, Eye, Download, Ban
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import {
  PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, CartesianGrid, 
  Tooltip, Legend, ResponsiveContainer
} from 'recharts';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

export default function MasterAdminTab({ token }) {
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [admins, setAdmins] = useState([]);
  const [showCreateAdmin, setShowCreateAdmin] = useState(false);
  const [adminForm, setAdminForm] = useState({
    email: '',
    password: '',
    full_name: '',
    admin_type: 'exam_admin',
    phone: ''
  });

  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    fetchData();
  }, [token]); // eslint-disable-line

  const fetchData = async () => {
    try {
      const [statsRes, adminsRes] = await Promise.all([
        axios.get(`${API}/master-admin/global-statistics`, { headers }),
        axios.get(`${API}/master-admin/admins`, { headers })
      ]);
      setStats(statsRes.data);
      setAdmins(adminsRes.data);
    } catch (err) {
      toast.error('Failed to load master admin data');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateAdmin = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/master-admin/create-admin`, adminForm, { headers });
      toast.success('Admin created successfully');
      setShowCreateAdmin(false);
      setAdminForm({ email: '', password: '', full_name: '', admin_type: 'exam_admin', phone: '' });
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create admin');
    }
  };

  const handleDeleteAdmin = async (adminId) => {
    if (!window.confirm('Are you sure you want to delete this admin?')) return;
    try {
      await axios.delete(`${API}/master-admin/admins/${adminId}`, { headers });
      toast.success('Admin deleted');
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete admin');
    }
  };

  const COLORS = ['#22c55e', '#ef4444', '#f59e0b', '#3b82f6'];

  if (loading) {
    return <div className="flex items-center justify-center py-12"><div className="animate-spin h-8 w-8 border-4 border-[#1B2A4A] border-t-transparent rounded-full"></div></div>;
  }

  const passFailData = [
    { name: 'Passed', value: stats?.examinations?.passed || 0 },
    { name: 'Failed', value: stats?.examinations?.failed || 0 }
  ];

  const attendanceData = [
    { name: 'Present', value: stats?.attendance?.present || 0 },
    { name: 'Absent', value: stats?.attendance?.absent || 0 }
  ];

  return (
    <div className="space-y-6" data-testid="master-admin-tab">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-[#1B2A4A] flex items-center gap-2">
            <Shield className="h-5 w-5 text-[#C9A227]" /> Master Admin Dashboard
          </h3>
          <p className="text-sm text-slate-500">Global system overview and admin management</p>
        </div>
        <Dialog open={showCreateAdmin} onOpenChange={setShowCreateAdmin}>
          <DialogTrigger asChild>
            <Button className="bg-[#1B2A4A] hover:bg-[#2a3d5f]" data-testid="create-admin-btn">
              <UserPlus className="h-4 w-4 mr-2" /> Create Admin
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Create New Admin</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleCreateAdmin} className="space-y-4">
              <div>
                <Label>Full Name *</Label>
                <Input value={adminForm.full_name} onChange={(e) => setAdminForm({...adminForm, full_name: e.target.value})} required />
              </div>
              <div>
                <Label>Email *</Label>
                <Input type="email" value={adminForm.email} onChange={(e) => setAdminForm({...adminForm, email: e.target.value})} required />
              </div>
              <div>
                <Label>Password *</Label>
                <Input type="password" value={adminForm.password} onChange={(e) => setAdminForm({...adminForm, password: e.target.value})} required />
              </div>
              <div>
                <Label>Admin Type *</Label>
                <Select value={adminForm.admin_type} onValueChange={(v) => setAdminForm({...adminForm, admin_type: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="exam_admin">Exam Admin</SelectItem>
                    <SelectItem value="accreditation_admin">Accreditation Admin</SelectItem>
                    <SelectItem value="finance_admin">Finance Admin</SelectItem>
                    <SelectItem value="curriculum_admin">Curriculum Admin</SelectItem>
                    <SelectItem value="master_admin">Master Admin</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Phone</Label>
                <Input value={adminForm.phone} onChange={(e) => setAdminForm({...adminForm, phone: e.target.value})} />
              </div>
              <div className="flex justify-end gap-2">
                <Button type="button" variant="outline" onClick={() => setShowCreateAdmin(false)}>Cancel</Button>
                <Button type="submit" className="bg-[#1B2A4A]">Create Admin</Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Global Statistics Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
        <Card className="border-l-4 border-l-blue-500">
          <CardContent className="p-4">
            <div className="flex items-center gap-2">
              <Users className="h-6 w-6 text-blue-500" />
              <div>
                <p className="text-xl font-bold text-[#1B2A4A]">{stats?.users?.total || 0}</p>
                <p className="text-xs text-slate-500">Total Users</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-purple-500">
          <CardContent className="p-4">
            <div className="flex items-center gap-2">
              <Award className="h-6 w-6 text-purple-500" />
              <div>
                <p className="text-xl font-bold text-[#1B2A4A]">{stats?.users?.examiners || 0}</p>
                <p className="text-xs text-slate-500">Examiners</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-green-500">
          <CardContent className="p-4">
            <div className="flex items-center gap-2">
              <CheckCircle className="h-6 w-6 text-green-500" />
              <div>
                <p className="text-xl font-bold text-[#1B2A4A]">{stats?.examinations?.pass_rate || 0}%</p>
                <p className="text-xs text-slate-500">Pass Rate</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-amber-500">
          <CardContent className="p-4">
            <div className="flex items-center gap-2">
              <Calendar className="h-6 w-6 text-amber-500" />
              <div>
                <p className="text-xl font-bold text-[#1B2A4A]">{stats?.attendance?.attendance_rate || 0}%</p>
                <p className="text-xs text-slate-500">Attendance</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-indigo-500">
          <CardContent className="p-4">
            <div className="flex items-center gap-2">
              <FileText className="h-6 w-6 text-indigo-500" />
              <div>
                <p className="text-xl font-bold text-[#1B2A4A]">{stats?.certificates?.valid || 0}</p>
                <p className="text-xs text-slate-500">Certificates</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-rose-500">
          <CardContent className="p-4">
            <div className="flex items-center gap-2">
              <Shield className="h-6 w-6 text-rose-500" />
              <div>
                <p className="text-xl font-bold text-[#1B2A4A]">{stats?.users?.admins || 0}</p>
                <p className="text-xs text-slate-500">Admins</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <Tabs defaultValue="overview" className="w-full">
        <TabsList className="bg-slate-100 p-1">
          <TabsTrigger value="overview" className="text-sm rounded-sm data-[state=active]:bg-[#1B2A4A] data-[state=active]:text-white">
            <BarChart2 className="h-4 w-4 mr-1" /> Overview
          </TabsTrigger>
          <TabsTrigger value="admins" className="text-sm rounded-sm data-[state=active]:bg-[#1B2A4A] data-[state=active]:text-white">
            <Shield className="h-4 w-4 mr-1" /> Admins
          </TabsTrigger>
          <TabsTrigger value="revenue" className="text-sm rounded-sm data-[state=active]:bg-[#1B2A4A] data-[state=active]:text-white">
            <DollarSign className="h-4 w-4 mr-1" /> Revenue
          </TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview" className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Pass/Fail Chart */}
            <Card>
              <CardContent className="p-4">
                <h4 className="font-medium mb-4 text-[#1B2A4A]">Pass / Fail Ratio</h4>
                <ResponsiveContainer width="100%" height={200}>
                  <PieChart>
                    <Pie data={passFailData} cx="50%" cy="50%" innerRadius={50} outerRadius={80} paddingAngle={5} dataKey="value" label>
                      {passFailData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            {/* Attendance Chart */}
            <Card>
              <CardContent className="p-4">
                <h4 className="font-medium mb-4 text-[#1B2A4A]">Present / Absent</h4>
                <ResponsiveContainer width="100%" height={200}>
                  <PieChart>
                    <Pie data={attendanceData} cx="50%" cy="50%" innerRadius={50} outerRadius={80} paddingAngle={5} dataKey="value" label>
                      {attendanceData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            {/* Exam Performance */}
            <Card className="md:col-span-2">
              <CardContent className="p-4">
                <h4 className="font-medium mb-4 text-[#1B2A4A]">Exam-wise Performance</h4>
                <ResponsiveContainer width="100%" height={250}>
                  <BarChart data={stats?.exam_performance || []}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="_id" />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    <Bar dataKey="passed" fill="#22c55e" name="Passed" />
                    <Bar dataKey="failed" fill="#ef4444" name="Failed" />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </div>

          {/* User Breakdown */}
          <Card>
            <CardContent className="p-4">
              <h4 className="font-medium mb-4 text-[#1B2A4A]">User Distribution</h4>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                <div className="text-center p-3 bg-slate-50 rounded">
                  <p className="text-2xl font-bold text-[#1B2A4A]">{stats?.users?.candidates || 0}</p>
                  <p className="text-xs text-slate-500">Candidates</p>
                </div>
                <div className="text-center p-3 bg-slate-50 rounded">
                  <p className="text-2xl font-bold text-[#1B2A4A]">{stats?.users?.examiners || 0}</p>
                  <p className="text-xs text-slate-500">Examiners</p>
                </div>
                <div className="text-center p-3 bg-slate-50 rounded">
                  <p className="text-2xl font-bold text-[#1B2A4A]">{stats?.users?.exam_centers || 0}</p>
                  <p className="text-xs text-slate-500">Exam Centers</p>
                </div>
                <div className="text-center p-3 bg-slate-50 rounded">
                  <p className="text-2xl font-bold text-[#1B2A4A]">{stats?.users?.institutions || 0}</p>
                  <p className="text-xs text-slate-500">Institutions</p>
                </div>
                <div className="text-center p-3 bg-slate-50 rounded">
                  <p className="text-2xl font-bold text-[#1B2A4A]">{stats?.users?.admins || 0}</p>
                  <p className="text-xs text-slate-500">Admins</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Admins Tab */}
        <TabsContent value="admins">
          <Card>
            <CardContent className="p-4">
              <h4 className="font-medium mb-4 text-[#1B2A4A]">Admin Users</h4>
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50">
                    <TableHead>Name</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Created</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {admins.map((admin) => (
                    <TableRow key={admin.id}>
                      <TableCell className="font-medium">{admin.full_name}</TableCell>
                      <TableCell>{admin.email}</TableCell>
                      <TableCell>
                        <Badge className={
                          admin.admin_type === 'master_admin' ? 'bg-purple-100 text-purple-700' :
                          admin.admin_type === 'exam_admin' ? 'bg-blue-100 text-blue-700' :
                          admin.admin_type === 'finance_admin' ? 'bg-green-100 text-green-700' :
                          'bg-slate-100'
                        }>
                          {admin.admin_type?.replace('_', ' ').toUpperCase() || 'ADMIN'}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <Badge className={admin.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}>
                          {admin.status || 'active'}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-sm text-slate-500">
                        {admin.created_at ? new Date(admin.created_at).toLocaleDateString() : '-'}
                      </TableCell>
                      <TableCell>
                        <Button size="sm" variant="destructive" className="h-7 px-2" onClick={() => handleDeleteAdmin(admin.id)}>
                          <Trash2 className="h-3 w-3" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Revenue Tab */}
        <TabsContent value="revenue">
          <Card>
            <CardContent className="p-6 text-center">
              <DollarSign className="h-12 w-12 mx-auto mb-4 text-slate-300" />
              <h4 className="font-medium text-[#1B2A4A] mb-2">Revenue Dashboard</h4>
              <p className="text-sm text-slate-500 mb-4">Payment integration pending (Razorpay)</p>
              <div className="grid grid-cols-3 gap-4 mt-6">
                <div className="p-4 bg-slate-50 rounded">
                  <p className="text-lg font-bold text-[#1B2A4A]">10%</p>
                  <p className="text-xs text-slate-500">Examiner Share</p>
                </div>
                <div className="p-4 bg-slate-50 rounded">
                  <p className="text-lg font-bold text-[#1B2A4A]">30%</p>
                  <p className="text-xs text-slate-500">Exam Center Share</p>
                </div>
                <div className="p-4 bg-slate-50 rounded">
                  <p className="text-lg font-bold text-[#1B2A4A]">60%</p>
                  <p className="text-xs text-slate-500">IMCAC Share</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
